/**
 * Appearance model (M6, T-53, Q-015; DESIGN_SYSTEM.md §2.7). Pure, so the unit
 * tests and the no-flash script in index.html can share its rules:
 *  - preference: `system | light | dark`, remembered per browser;
 *  - the default is **light** for this release (Q-015: dark is opt-in);
 *  - `system` follows `prefers-color-scheme` live.
 * index.html repeats THEME_STORAGE_KEY and the rules inline (it runs before any
 * bundle); `themeModel.test.ts` keeps the two in step.
 */

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'ote-appearance';
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light';
/** Menu order: מערכת · בהיר · כהה. */
export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];
export const DARK_QUERY = '(prefers-color-scheme: dark)';

export function parseThemePreference(raw: unknown): ThemePreference {
  return raw === 'system' || raw === 'light' || raw === 'dark' ? raw : DEFAULT_THEME_PREFERENCE;
}

export function resolveTheme(preference: ThemePreference, systemDark: boolean): ResolvedTheme {
  if (preference === 'system') return systemDark ? 'dark' : 'light';
  return preference;
}

type ReadStore = Pick<Storage, 'getItem'>;
type WriteStore = Pick<Storage, 'setItem' | 'removeItem'>;

/** The stored preference; storage can be missing or throw (private mode, blocked site data). */
export function readThemePreference(storage: ReadStore | null | undefined): ThemePreference {
  try {
    return parseThemePreference(storage?.getItem(THEME_STORAGE_KEY));
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
}

/** Remembers the preference; the default is stored as "no value". Never throws. */
export function writeThemePreference(storage: WriteStore | null | undefined, preference: ThemePreference): void {
  try {
    if (preference === DEFAULT_THEME_PREFERENCE) storage?.removeItem(THEME_STORAGE_KEY);
    else storage?.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    /* not remembered: the choice still applies to this page */
  }
}

/** "15 19 32" (a token's RGB channels) → "rgb(15 19 32)"; null when it is not three channels. */
export function channelsToCssColor(channels: string | null | undefined): string | null {
  const parts = (channels ?? '').trim().split(/[\s,]+/).filter(Boolean);
  if (parts.length !== 3 || parts.some((p) => !/^\d{1,3}$/.test(p) || Number(p) > 255)) return null;
  return `rgb(${parts.join(' ')})`;
}
